/**
 * Practice configuration, regulatory pack upgrades and the Fixed Asset Register projection
 * (api/v1/PracticePacks and api/v1/FixedAssetRegister).
 */

// ---- Vocabulary (mirrors the API enums) ----

/** What a customisation does to the base item it names. */
export enum PackItemAction {
  Override = 0,
  Add = 1,
  Suppress = 2,
}

/** How the newer regulatory pack differs from the one the practice layer was built on. */
export enum PackChangeKind {
  Added = 0,
  Modified = 1,
  Removed = 2,
}

/** An authorised decision about one conflicting change. */
export enum PackConflictResolution {
  KeepPractice = 0,
  TakeBase = 1,
  Merge = 2,
}

export enum PracticePackStatus {
  Draft = 0,
  Published = 1,
  Superseded = 2,
}

export enum PackUpgradeStatus {
  Open = 0,
  Resolved = 1,
  Published = 2,
  Cancelled = 3,
}

/** Which layer supplied the value that wins for an item. */
export enum PackLayer {
  Base = 0,
  Practice = 1,
  Client = 2,
}

/** The semantic identities an upgrade compares by. */
export const PACK_ITEM_TYPES = ['ReportingCode', 'DisclosureField', 'Rule', 'Template'] as const;
export type PackItemType = (typeof PACK_ITEM_TYPES)[number];

// ---- Practice packs ----

export interface PracticePackRow {
  id: string;
  frameworkCode: string;
  basePackVersionId: string;
  basePackName: string | null;
  versionNo: number;
  name: string;
  status: PracticePackStatus;
  statusText: string;
  itemCount: number;
  publishedAtUtc: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
  eTag: string;
}

export interface CreatePracticePackPayload {
  frameworkCode: string;
  name: string | null;
}

/** One item across all three layers: base, practice customisation, client override. */
export interface PackLayerRow {
  itemType: string;
  itemKey: string;
  baseJson: string | null;
  practiceAction: PackItemAction | null;
  practiceJson: string | null;
  practiceNotes: string | null;
  clientAction: PackItemAction | null;
  clientJson: string | null;
  clientReason: string | null;
  effectiveLayer: PackLayer;
  effectiveJson: string | null;
  isSuppressed: boolean;
}

export interface SavePracticePackItemPayload {
  practicePackId: string;
  itemType: string;
  itemKey: string;
  action: PackItemAction;
  payloadJson: string | null;
  notes: string | null;
}

// ---- Client overrides ----

export interface ClientOverrideRow {
  id: string;
  clientId: string;
  frameworkCode: string;
  itemType: string;
  itemKey: string;
  action: PackItemAction;
  payloadJson: string | null;
  reason: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
}

export interface SaveClientOverridePayload {
  clientId: string;
  frameworkCode: string;
  itemType: string;
  itemKey: string;
  action: PackItemAction;
  payloadJson: string | null;
  reason: string;
}

// ---- Upgrade, diff and conflicts ----

export interface PackUpgradeSummary {
  id: string;
  frameworkCode: string;
  practicePackId: string;
  fromBasePackVersionId: string;
  toBasePackVersionId: string;
  toBaseName: string | null;
  status: PackUpgradeStatus;
  statusText: string;
  changeCount: number;
  conflictCount: number;
  unresolvedConflicts: number;
  publishedPackId: string | null;
  comparedAtUtc: string;
  eTag: string;
}

export interface PackConflictRow {
  id: string;
  itemType: string;
  itemKey: string;
  changeKind: PackChangeKind;
  changeKindText: string;
  isConflict: boolean;
  summary: string | null;
  baseBeforeJson: string | null;
  baseAfterJson: string | null;
  practiceJson: string | null;
  resolution: PackConflictResolution | null;
  resolutionText: string | null;
  resolutionNote: string | null;
  mergedPayloadJson: string | null;
  resolvedAtUtc: string | null;
  eTag: string;
}

export interface PackUpgradeDetail {
  upgrade: PackUpgradeSummary;
  fromBaseName: string | null;
  practiceName: string | null;
  practiceVersionNo: number;
  conflicts: PackConflictRow[];
  nonConflictingChanges: PackConflictRow[];
  latestRegressionRun: GoldenRunResult | null;
  canPublish: boolean;
  blockedReason: string | null;
}

export interface CompareUpgradePayload {
  practicePackId: string;
  toBasePackVersionId: string | null;
}

export interface ResolvePackConflictPayload {
  packConflictId: string;
  resolution: PackConflictResolution;
  resolutionNote: string | null;
  mergedPayloadJson: string | null;
  expectedETag: string | null;
}

export interface ResolvePackConflictsPayload {
  packUpgradeId: string;
  resolutions: ResolvePackConflictPayload[];
}

// ---- Preview and regression ----

export interface PackPreviewRow {
  itemType: string;
  itemKey: string;
  action: PackItemAction;
  layer: PackLayer;
  payloadJson: string | null;
  outcome: string;
  isConflict: boolean;
  resolution: PackConflictResolution | null;
}

export interface GoldenFixtureRow {
  id: string;
  fixtureCode: string;
  name: string;
  frameworkCode: string;
  entityTypeCode: string;
  outputProfileCode: string;
  taxonomyRegulator: string | null;
  isActive: boolean;
}

export interface GoldenFixtureResult {
  fixtureCode: string;
  name: string;
  frameworkCode: string;
  entityTypeCode: string;
  outputProfileCode: string;
  passed: boolean;
  missingReportingCodes: string[];
  missingDisclosureFields: string[];
  missingRules: string[];
  message: string;
}

export interface GoldenRunResult {
  id: string;
  packUpgradeId: string;
  passed: boolean;
  passedCount: number;
  failedCount: number;
  results: GoldenFixtureResult[];
  runAtUtc: string;
}

// ---- Publish ----

export interface PublishPracticePackPayload {
  packUpgradeId: string;
  expectedETag: string | null;
}

export interface PublishPracticePackResponse {
  practicePackId: string;
  versionNo: number;
  message: string;
}

// ---- Effective (pinned) pack ----

export interface EffectivePackInfo {
  id: string;
  accountingPeriodId: string | null;
  frameworkCode: string;
  basePackVersionId: string;
  basePackName: string | null;
  practicePackId: string | null;
  practicePackVersionNo: number | null;
  itemCount: number;
  checksum: string;
  createdAtUtc: string;
}

// ---- Fixed Asset Register ----

export interface FixedAssetMovementRow {
  id: string;
  sourceAssetId: string;
  sourceClassId: string | null;
  classCode: string;
  className: string;
  assetName: string;
  reportingCode: string | null;
  depreciationMethod: string | null;
  depreciationRate: number | null;
  costBroughtForward: number;
  additions: number;
  disposals: number;
  revaluation: number;
  costCarriedForward: number;
  depnBroughtForward: number;
  depnCharge: number;
  depnOnDisposals: number;
  depnCarriedForward: number;
  acquiredOn: string | null;
  disposedOn: string | null;
  sourceVersion: string | null;
  netBookValue: number;
}

export interface FixedAssetClassTotal {
  classCode: string;
  className: string;
  assetCount: number;
  costBroughtForward: number;
  additions: number;
  disposals: number;
  revaluation: number;
  costCarriedForward: number;
  depnBroughtForward: number;
  depnCharge: number;
  depnOnDisposals: number;
  depnCarriedForward: number;
  netBookValue: number;
  priorNetBookValue: number;
}

/** Something a later sync invalidated and that needs revalidation. */
export interface FixedAssetStaleRow {
  id: string;
  objectType: string;
  objectId: string;
  reason: string;
  syncVersion: number;
  staleSinceUtc: string;
}

export interface FixedAssetSyncView {
  syncId: string | null;
  accountingPeriodId: string;
  syncVersion: number | null;
  sourceModule: string | null;
  syncedAtUtc: string | null;
  assetCount: number;
  scheduleStatus: string | null;
  /** False when no Fixed Asset Register endpoint is configured for this environment. */
  isConfigured: boolean;
  message: string | null;
  movements: FixedAssetMovementRow[];
  classTotals: FixedAssetClassTotal[];
  staleObjects: FixedAssetStaleRow[];
}

export interface FixedAssetSyncResponse {
  syncId: string;
  syncVersion: number;
  assetCount: number;
  staleCount: number;
  message: string;
  view: FixedAssetSyncView;
}

// ---- cap3-e: depreciation fallback when no Fixed Asset Register is configured ----

/** Depreciation method: 0 straight line, 1 reducing balance. */
export interface DepreciationPolicyRow {
  classCode: string;
  className: string;
  isIntangible: boolean;
  method: number;
  methodName: string;
  ratePercent: number;
  isClientPolicy: boolean;
  defaultMethod: number;
  defaultRatePercent: number;
}

export interface DepreciationPolicyView {
  clientId: string;
  clientName: string;
  classes: DepreciationPolicyRow[];
}

export interface SaveDepreciationPoliciesPayload {
  clientId: string;
  policies: { classCode: string; method: number; ratePercent: number }[];
}

export interface FixedAssetDisposalPayloadRow {
  classCode: string;
  disposalProceeds: number;
  disposedCost: number;
  disposedDepreciation: number;
}

export interface DepreciationAccountRow {
  nominalAccountId: string | null;
  code: string;
  name: string;
  role: string;
  sourceAmount: number;
  priorAmount: number;
  journalAmount: number;
  isInPeriod: boolean;
}

export interface DepreciationClassCalculation {
  classCode: string;
  className: string;
  method: string;
  ratePercent: number;
  rateSource: string;
  costBroughtForward: number;
  additions: number;
  disposals: number;
  costCarriedForward: number;
  depnBroughtForward: number;
  depnCharge: number;
  depnOnDisposals: number;
  depnCarriedForward: number;
  netBookValue: number;
  chargeAlreadyInLedger: number;
  journalCharge: number;
  disposalProceeds: number;
  disposalProfitOrLoss: number;
  accounts: DepreciationAccountRow[];
}

export interface DepreciationCalculationView {
  accountingPeriodId: string;
  startDate: string;
  endDate: string;
  periodFactor: number;
  source: string;
  registerConfigured: boolean;
  classes: DepreciationClassCalculation[];
  totalCharge: number;
  totalJournalCharge: number;
  totalDisposalProfitOrLoss: number;
  expenseAccounts: DepreciationAccountRow[];
  warnings: string[];
}

export interface CreateDepreciationJournalPayload {
  journalDate?: string | null;
  expenseNominalAccountId?: string | null;
  narrative?: string | null;
}

export interface DepreciationJournalResponse {
  journalId: string;
  journalNo: number;
  amount: number;
  message: string;
}
